import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import DatabaseForegroundPanel from "../components/indexes/DatabaseForegroundPanel";
import { getDatabases } from "../api/adminApi";
import LifecyclePanel from "../components/lifecycle/LifecyclePanel";
import PartitioningPanel from "../components/lifecycle/PartitioningPanel";
import { Hero } from "../components/adminSurface/AdminSurface";
import { RecycleArt } from "../components/adminSurface/heroArt";
import { useLicense } from "../licensing/LicenseContext";

// Master–detail lifecycle management: a database rail on the left (like the Indexes page),
// and the selected database's compliance guard + rules on the right.
export default function LifecycleRulesPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { hasFeature } = useLicense();

  const [databases, setDatabases] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      setLoading(true); setError("");
      try {
        const data = await getDatabases(token);
        const list = Array.isArray(data) ? data : (data?.databases ?? data?.Databases ?? []);
        if (!active) return;
        setDatabases(list);
        if (list.length > 0) setSelectedId(String(dbId(list[0])));
      } catch (e) {
        if (active) setError(e?.message || String(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const selected = useMemo(
    () => databases.find((d) => String(dbId(d)) === String(selectedId)) || null,
    [databases, selectedId]
  );

  const { active, archived } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? databases.filter((d) => String(dbName(d)).toLowerCase().includes(q) || String(dbId(d)).includes(q)) : databases;
    return {
      active: list.filter((d) => !(d.isClosed || d.IsClosed)),
      archived: list.filter((d) => d.isClosed || d.IsClosed),
    };
  }, [databases, search]);

  const guardLabel = (d) => {
    const g = parseGuard(d.propertiesJson ?? d.PropertiesJson);
    if (g.legalHold) return t("lifecycle.cardLegalHold");
    if (g.minVal) return t("lifecycle.cardKeep", { value: g.minVal, unit: t("lifecycle.unit" + (g.minUnit || "Years")) });
    return null;
  };

  const renderTab = (d) => {
    const id = dbId(d);
    const isActive = String(id) === String(selectedId);
    const isClosed = d.isClosed || d.IsClosed || false;
    return (
      <div key={id} className={`dbTabRow${isClosed ? " isArchived" : ""}`}>
        <button type="button" className={`dbTab${isActive ? " isActive" : ""}${isClosed ? " isClosed" : ""}`} onClick={() => setSelectedId(String(id))}>
          <span className="dbTabLabel">
            <span className="dbTabLabelText">{dbName(d)}</span>
            {isActive && !isClosed && guardLabel(d) ? <span className="dbTabLabelCount">{guardLabel(d)}</span> : null}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <div className="page-container">
        <div className="indexes-page">
          <Hero icon="♻️" title={t("lifecycle.managementTitle")} subtitle={t("lifecycle.pageSubtitle")} art={<RecycleArt />} />

          <div className="indexes-shell">
            <div className="indexes-card">
              <div className="indexesShell">
                {/* ── Database rail ── */}
                <div className="db-column dbRail">
                  <div className="dbRailHeader">
                    <div className="db-stack-header">
                      <div className="db-track-label"><span>{t("admin.database", { defaultValue: "Databases" })}</span></div>
                      <div className="db-stack-search-wrapper">
                        <input className="db-stack-search" type="search" value={search}
                               onChange={(e) => setSearch(e.target.value)}
                               placeholder={t("admin.searchDatabases", { defaultValue: "Search databases..." })} />
                      </div>
                    </div>
                    {loading && databases.length === 0 ? <div className="db-track-empty">{t("common.loading")}</div> : null}
                    {!loading && active.length === 0 && archived.length === 0 ? <div className="db-track-empty">{t("lifecycle.noDatabases")}</div> : null}
                  </div>
                  <div className="dbTabsRegion">
                    <div className="dbTabStack">
                      {active.map(renderTab)}
                      {archived.length > 0 && (
                        <>
                          <div className="dbArchivedDivider"><span>{t("admin.archived", { defaultValue: "Archived" })}</span></div>
                          {archived.map(renderTab)}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Detail panel ── */}
                <DatabaseForegroundPanel hasSelection={!!selected}>
                  <div className="indexes-main">
                    {error ? <div className="arch-alert arch-alert--error" role="alert">{error}</div> : null}

                    <div className="indexes-detail-header">
                      <div>
                        <div className="indexes-detail-label">{t("admin.selectedDatabaseLabel", { defaultValue: "Selected database" })}</div>
                        <div className="indexes-detail-name">{selected ? dbName(selected) : "—"}</div>
                      </div>
                      <div className="indexes-detail-header-right">
                        {hasFeature("content-governance") && (
                          <Link to="/admin/legal-holds" className="gov-chip gov-chip--holds">
                            <span className="gov-chip__ico">🔒</span>{t("holds.title")}
                          </Link>
                        )}
                        {hasFeature("content-governance") && (
                          <Link to="/admin/disposition" className="gov-chip gov-chip--disp">
                            <span className="gov-chip__ico">♻️</span>{t("disposition.title")}
                          </Link>
                        )}
                        {hasFeature("content-governance") && (
                          <Link to="/admin/classification" className="gov-chip gov-chip--class">
                            <span className="gov-chip__ico">🏷️</span>{t("classification.title")}
                          </Link>
                        )}
                        {hasFeature("content-governance") && (
                          <Link to="/admin/content-organization" className="gov-chip gov-chip--org">
                            <span className="gov-chip__ico">🗂️</span>{t("contentOrg.title", { defaultValue: "Content Organization" })}
                          </Link>
                        )}
                        <Link to="/admin/lifecycle-events" className="gov-chip gov-chip--events">
                          {t("lifecycle.viewEvents")}<span className="gov-chip__ico gov-chip__arrow">→</span>
                        </Link>
                      </div>
                    </div>

                    {selected ? (
                      <>
                        <PartitioningPanel
                          key={`pt-${dbId(selected)}`}
                          token={token}
                          databaseId={dbId(selected)}
                          databaseName={dbName(selected)}
                        />
                        <LifecyclePanel
                          key={dbId(selected)}
                          token={token}
                          databaseId={dbId(selected)}
                          databaseName={dbName(selected)}
                          propertiesJson={selected.propertiesJson ?? selected.PropertiesJson ?? ""}
                        />
                      </>
                    ) : (
                      <div className="indexes-empty-state" style={{ padding: "48px 24px", textAlign: "center", color: "#6b7280" }}>
                        {t("lifecycle.pickDatabase")}
                      </div>
                    )}
                  </div>
                </DatabaseForegroundPanel>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Read the compliance guard (legal hold + min-retention) out of a DB's PropertiesJson.
function parseGuard(propertiesJson) {
  try {
    const g = JSON.parse(propertiesJson || "{}")?.retentionGuard || {};
    return { legalHold: !!g.legalHold, minVal: g.minRetentionValue, minUnit: g.minRetentionUnit };
  } catch { return {}; }
}

// GetDatabasesResponse serializes camelCase: { id, name, displayName, propertiesJson, ... }
function dbId(d) { return d?.id ?? d?.Id ?? d?.dbInfoId ?? d?.DBInfoId; }
function dbName(d) {
  return d?.name ?? d?.Name ?? d?.displayName ?? d?.DisplayName
    ?? d?.dbName ?? d?.DbName ?? String(dbId(d));
}
